import { getUploadMimeType, prepareImageUpload } from "~/utils/image-upload";
import { DBFactory } from "~/classes/database/db_factory";
import { BaseModel } from "~/classes/models/base.model";
import { SheetController } from "~/components/Base/Sheet/controller";
import { Assignment, Assignments } from "../assignments/assignment.model";
import { Classroom, Classrooms } from "../classrooms/classroom.model";
import { Student, Students } from "../students/student.model";
const { createDocument, updateDocumentOriginalFileMediaHref } = useDocuments();

export enum SubmissionJobType {
  standard = "standard",
  additionalInformation = "additionalInformation",
  additionalFiles = "additionalFiles",
  review = "review",
}

type SubmissionJobConstructorParams = {
  id: string;
  userId: string;
  profileId?: string;
  sheetController: SheetController;
  checklists?: Checklists;
  checklist?: Checklist;

  classrooms?: Classrooms;
  classroom?: Classroom;
  students?: Students;
  student?: Student;
  assignments?: Assignments;
  assignment?: Assignment;

  uploadMethod: "google" | "computer" | "create";
  parseBehavior: "default" | "convertToHtml" | "convertToQuill";

  documentSubmissionType: DocumentSubmissionType;
  submittedDocumentState: DocumentState;
  attachChecklist: boolean;
  accessToken: string;

  files: any[];

  studentSubmissionOps?: any;
  type?: SubmissionJobType;
  documentIds?: string[];

  requiresReview?: boolean;
  autoOpen?: boolean;

  enableStudentThesaurus?: boolean;
  enableStudentReview?: boolean;
  extractWritingCompanionChecklist?: boolean;
};

export type SubmissionJobs = SubmissionJob[];

export class SubmissionJob extends BaseModel {
  id: string;
  userId: string;
  profileId?: string;
  sheetController: SheetController;
  checklists?: Checklists;
  checklist?: Checklist;
  classroom?: Classroom;
  classrooms?: Classrooms;
  students?: Students;
  student?: Student;
  assignments?: Assignments;
  assignment?: Assignment;
  uploadMethod: "google" | "computer" | "create";
  parseBehavior: "default" | "convertToHtml" | "convertToQuill";
  accessToken: string;

  documentSubmissionType: DocumentSubmissionType;
  submittedDocumentState: DocumentState;
  attachChecklist: boolean;
  filesBeingUploaded: any;
  documentIds?: string[];
  files: any[];
  autoOpen: boolean;

  studentSubmissionOps?: any;

  type: SubmissionJobType;

  requiresReview: boolean;
  enableStudentThesaurus: boolean;
  enableStudentReview: boolean;
  extractWritingCompanionChecklist: boolean;

  constructor(data: SubmissionJobConstructorParams) {
    super(data);
    this.id = data.id;
    this.userId = data.userId;
    this.profileId = data.profileId;
    this.sheetController = data.sheetController;
    this.checklists = data.checklists;
    this.checklist = data.checklist;
    this.classrooms = data.classrooms;
    this.classroom = data.classroom;
    this.students = data.students;
    this.student = data.student;
    this.assignments = data.assignments;
    this.assignment = data.assignment;
    this.uploadMethod = data.uploadMethod;
    this.accessToken = data.accessToken;
    this.documentSubmissionType = data.documentSubmissionType;
    this.submittedDocumentState = data.submittedDocumentState;
    this.attachChecklist = data.attachChecklist;
    this.parseBehavior = data.parseBehavior ?? "default";

    this.files = data.files;
    this.filesBeingUploaded = ref([]);
    this.studentSubmissionOps = data.studentSubmissionOps;
    this.type = data.type ?? SubmissionJobType.standard;
    this.requiresReview = data.requiresReview ?? false;
    this.autoOpen = data.autoOpen ?? true;
    this.enableStudentSubmissionCreation =
      data.enableStudentSubmissionCreation ?? false;
    this.enableStudentThesaurus = data.enableStudentThesaurus ?? false;
    this.enableStudentReview = data.enableStudentReview ?? false;
    this.extractWritingCompanionChecklist =
      data.extractWritingCompanionChecklist ?? false;
  }

  hasMultipleImages = () => {
    return (
      this.files.filter((file) => {
        return getUploadMimeType(file).startsWith("image/");
      }).length > 1
    );
  };

  createWritableFile = async (
    fileName: string,
    options?: {
      preventNavigation?: boolean;
    }
  ) => {
    let checklists: Checklists = this.checklists ?? [];
    let checklist: Checklist | undefined = this.checklist;

    const documentParams = useCreateDocumentParams({
      classroom: this.classroom,
      assignment: this.assignment,
      student: this.student,
      checklist: checklist,
      checklists: checklists,
      documentSubmissionType: this.documentSubmissionType,
      attachChecklist: this.attachChecklist,
      userId: this.userId,
      profileId: this.profileId,
      enableStudentThesaurus: this.enableStudentThesaurus,
      enableStudentReview: this.enableStudentReview,
    });

    const db = DBFactory.createDatabase();

    const documentId = await createDocument(fileName, {
      ...documentParams,
      documentState: DocumentState.editing,
    });

    const submittedDocumentSnapshot = await db.get({
      path: `documents/${documentId}`,
      collection: `documents/${documentId}`,
    });

    const submittedDocument = {
      ...submittedDocumentSnapshot,
      id: documentId,
    } as SubmittedDocument;

    const revision = {
      id: db.generateId({
        path: `documents/${documentId}/revisions`,
        collection: `documents/${documentId}/revisions`,
      }),
      documentId,
      externalId: documentId,
      lastUpdatedTimestamp: Date.now(),
      paragraphs: [],
      studentSubmissionOps: this.studentSubmissionOps ?? {},
    } as Revision;

    await db.save(revision, {
      path: `documents/${documentId}/revisions/${revision.id}`,
      collection: `documents/${documentId}/revisions`,
    });

    if (
      submittedDocument.checklistId != undefined &&
      submittedDocument.checklistPath != undefined
    ) {
      await useApplyChecklistToDocuments().applyChecklistToDocumentNow(
        submittedDocument.checklistId,
        submittedDocument.checklistPath,
        documentId,
        false
      );
    }

    const path = useGenerateDocumentPath(
      submittedDocument,
      DocumentViewMode.student
    );

    if (options?.preventNavigation == true || this.autoOpen != true) {
      return documentId;
    }

    window.open(path, "_blank");
  };

  uploadFiles = async (onComplete?: (documentIds: string[]) => void) => {
    let checklists: Checklists = this.checklists ?? [];
    let checklist: Checklist | undefined = this.checklist;

    if (this.requiresReview) {
      this.type = SubmissionJobType.review;

      useSubmissionJobs().addSubmissionJob(this);
    }

    const documentParams = useCreateDocumentParams({
      userId: useCurrentUID() ?? "",
      profileId: this.profileId,
      classroom: this.classroom,
      assignment: this.assignment,
      student: this.student,
      checklist: checklist,
      checklists: checklists,
      documentSubmissionType: this.documentSubmissionType,
      attachChecklist: this.attachChecklist,
      enableStudentThesaurus: this.enableStudentThesaurus,
      enableStudentReview: this.enableStudentReview,
    });

    let combineImages = true;

    const wordLikeFiles = this.files.filter((file) => {
      return [
        "application/vnd.google-apps.document",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ].includes(file.type);
    });

    const googleSheetFiles = this.files.filter((file) => {
      return ["application/vnd.google-apps.spreadsheet"].includes(file.type);
    });

    const otherFiles = this.files.filter((file) => {
      let excludedFileTypes = [
        "application/vnd.google-apps.document",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.google-apps.spreadsheet",
      ];

      if (combineImages && getUploadMimeType(file).startsWith("image/") == true) {
        return false;
      }

      return excludedFileTypes.includes(file.type) != true;
    });

    let documentIds: string[] = [];

    let imageFiles = this.files
      .filter((file) => {
        return getUploadMimeType(file).startsWith("image/");
      });

    if (combineImages && imageFiles.length > 0) {
      let imageMediaObjects: MediaObject[] = [];

      const documentId = await createDocument(imageFiles[0].name, {
        ...documentParams,
        fileType: imageFiles[0].type,
      });

      documentIds.push(documentId);

      this.filesBeingUploaded.value.push(documentId);

      for (const imageFile of imageFiles) {
        const fileId = imageFile.fileId;
        documentParams.googleDriveFileId = fileId;

        // These files are easy, I just upload hte documetn to firebase storage and create a revision.
        let file: File | CustomFile | undefined = imageFile;

        if (this.uploadMethod == "google") {
          if (this.accessToken == undefined) {
            continue;
          }

          const response = await fetch(
            `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
            {
              headers: new Headers({
                Authorization: "Bearer " + this.accessToken,
              }),
            }
          );

          if (!response.ok) {
            throw new Error(
              `Server responded with ${response.status}: ${response.statusText}`
            );
          }

          const blob = await response.blob();

          const customFile = {
            data: blob,
            name: imageFile.name,
            size: blob.size,
            type: imageFile.type,
          } as CustomFile;

          file = customFile;
        }

        if (file instanceof File) file = await prepareImageUpload(file);

        const mediaObject = await useUploadSubmissionToFirestore({
          file,
          documentId,
        });

        if (mediaObject == undefined) {
          continue;
        }

        imageMediaObjects.push(mediaObject);
      }

      await useCreateMediaRevision({
        documentId,
        mediaObjects: imageMediaObjects,
        submittedDocumentState: this.requiresReview
          ? DocumentState.studentReview
          : this.submittedDocumentState,
      });

      this.filesBeingUploaded.value = [
        ...this.filesBeingUploaded.value.filter(
          (id: string) => id !== documentId
        ),
      ];
    }

    for (const googleSheetFile of googleSheetFiles) {
      const fileId = googleSheetFile.fileId;
      documentParams.googleDriveFileId = fileId;

      const documentId = await createDocument(googleSheetFile.name, {
        ...documentParams,
        fileType: googleSheetFile.type,
      });

      documentIds.push(documentId);

      this.filesBeingUploaded.value.push(documentId);

      // Export the google drive file as an excel file
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`,
        {
          headers: new Headers({
            Authorization: "Bearer " + this.accessToken,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Server responded with ${response.status}: ${response.statusText}`
        );
      }

      const blob = await response.blob();

      const customFile = {
        data: blob,
        name: `${googleSheetFile.name}.xlsx`,
        size: blob.size,
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      } as CustomFile;
      const mediaObject = await useUploadSubmissionToFirestore({
        file: customFile,
        documentId,
      });

      if (mediaObject == undefined) {
        continue;
      }

      await useCreateMediaRevision({
        documentId,
        mediaObject,
        submittedDocumentState: this.requiresReview
          ? DocumentState.studentReview
          : this.submittedDocumentState,
      });

      this.filesBeingUploaded.value = [
        ...this.filesBeingUploaded.value.filter(
          (id: string) => id !== documentId
        ),
      ];
    }

    for (const otherFile of otherFiles) {
      const fileId = otherFile.fileId;
      documentParams.googleDriveFileId = fileId;

      const documentId = await createDocument(otherFile.name, {
        ...documentParams,
        fileType: otherFile.type,
      });

      documentIds.push(documentId);

      this.filesBeingUploaded.value.push(documentId);

      // These files are easy, I just upload hte documetn to firebase storage and create a revision.
      let file: File | CustomFile | undefined = otherFile;

      if (this.uploadMethod == "google") {
        if (this.accessToken == undefined) {
          continue;
        }

        const response = await fetch(
          `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
          {
            headers: new Headers({
              Authorization: "Bearer " + this.accessToken,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(
            `Server responded with ${response.status}: ${response.statusText}`
          );
        }

        const blob = await response.blob();

        const customFile = {
          data: blob,
          name: otherFile.name,
          size: blob.size,
          type: otherFile.type,
        } as CustomFile;

        file = customFile;
      }

      const mediaObject = await useUploadSubmissionToFirestore({
        file,
        documentId,
      });

      if (mediaObject == undefined) {
        continue;
      }

      await useCreateMediaRevision({
        documentId,
        mediaObject,
        submittedDocumentState: this.requiresReview
          ? DocumentState.studentReview
          : this.submittedDocumentState,
      });

      this.filesBeingUploaded.value = [
        ...this.filesBeingUploaded.value.filter(
          (id: string) => id !== documentId
        ),
      ];
    }

    for (const wordLikeFile of wordLikeFiles) {
      documentParams.googleDriveFileId = wordLikeFile.fileId;

      const documentId = await createDocument(wordLikeFile.name, {
        ...documentParams,
        fileType: wordLikeFile.type,
      });

      documentIds.push(documentId);

      this.filesBeingUploaded.value.push(documentId);
      if (this.parseBehavior == "default") {
        const isLargeBulkUpload = wordLikeFiles.length > 5;
        const isLargeFile = wordLikeFile.size > 4.25 * 1024 * 1024;

        if (
          this.uploadMethod != "google" &&
          (isLargeBulkUpload || isLargeFile)
        ) {
          // I'm going to upload the file to firebase storage:
          useUploadSubmissionToFirestore({
            file: wordLikeFile,
            documentId,
          }).then(async (mediaObject) => {
            if (mediaObject == undefined) {
              this.filesBeingUploaded.value = [
                ...this.filesBeingUploaded.value.filter(
                  (id: string) => id !== documentId
                ),
              ];
              return;
            }

            // then here I'm going to create a task.
            await useCreateImportBulkSubmissionTask({
              documentId,
              mediaObject,
            });

            this.filesBeingUploaded.value = [
              ...this.filesBeingUploaded.value.filter(
                (id: string) => id !== documentId
              ),
            ];
          });

          continue;
        }

        if (this.uploadMethod == "google") {
          $fetch(`/api/documents/${documentId}/parse/google`, {
            method: "POST",
            headers: await useApiHeaders(),
            body: {
              fileId: wordLikeFile.fileId,
              fileName: wordLikeFile.name,
              fileType: wordLikeFile.type,
              submittedDocumentState:
                this.submittedDocumentState ?? DocumentState.submitted,
              accessToken: this.accessToken,
            },
          }).then(() => {
            this.filesBeingUploaded.value = [
              ...this.filesBeingUploaded.value.filter(
                (id: string) => id !== documentId
              ),
            ];
          });
          continue;
        }

        const formData = new FormData();

        formData.append("file", wordLikeFile);
        formData.append("fileName", wordLikeFile.name);
        formData.append("fileType", wordLikeFile.type);
        formData.append("method", this.uploadMethod);
        formData.append(
          "submittedDocumentState",
          this.submittedDocumentState ?? DocumentState.submitted
        );

        $fetch(`/api/documents/${documentId}/parse`, {
          method: "POST",
          headers: await useApiHeaders(),
          body: formData,
        }).finally(() => {
          this.filesBeingUploaded.value = [
            ...this.filesBeingUploaded.value.filter(
              (id: string) => id !== documentId
            ),
          ];
        });
      }

      if (this.parseBehavior == "convertToHtml") {
        let html: string | undefined = undefined;
        let headers: Revision["headers"] | undefined = undefined;
        let importedChecklist: Revision["importedChecklist"] | undefined =
          undefined;

        try {
          if (this.uploadMethod == "google") {
            const fileData = await useExportGoogleFile({
              fileId: wordLikeFile.fileId,
              fileName: wordLikeFile.name,
              fileType: wordLikeFile.type,
              accessToken: this.accessToken,
            });

            const conversionResult = await useConvertGoogleDocToHtmlWithHeaders(
              fileData,
              {
                extractWritingCompanionChecklist:
                  this.extractWritingCompanionChecklist,
              }
            );
            html = conversionResult?.html;
            headers = conversionResult?.headers;
            importedChecklist = conversionResult?.importedChecklist;
          } else {
            const conversionResult = await useConvertWordFileToHtmlWithHeaders(
              wordLikeFile,
              {
                extractWritingCompanionChecklist:
                  this.extractWritingCompanionChecklist,
              }
            );
            html = conversionResult.html;
            headers = conversionResult.headers;
            importedChecklist = conversionResult.importedChecklist;
          }
        } catch (error) {
          useBaseToast(
            "Oops! Something went wrong. Please try again or Report Issue.",
            "error"
          );

          console.log(error);
          // Update the document state to failed
          // await useDocuments().updateDocument({
          //   id: documentId,
          //   state: DocumentState.conversionFailed,
          // });

          console.error(error);
          this.filesBeingUploaded.value = [
            ...this.filesBeingUploaded.value.filter(
              (id: string) => id !== documentId
            ),
          ];

          documentIds = documentIds.filter((id) => id !== documentId);
          continue;
        }

        if (html == undefined) {
          this.filesBeingUploaded.value = [
            ...this.filesBeingUploaded.value.filter(
              (id: string) => id !== documentId
            ),
          ];
          continue;
        }

        // Create a revision with teh html embedded in it
        await useCreateHtmlRevision({
          documentId,
          html,
          headers,
          importedChecklist,
          submittedDocumentState:
            this.submittedDocumentState ?? DocumentState.submitted,
        });

        if (
          documentParams.checklist?.id != undefined &&
          documentParams.checklist?.path != undefined
        ) {
          await useApplyChecklistToDocuments().applyChecklistToDocumentNow(
            documentParams.checklist.id,
            documentParams.checklist.path,
            documentId,
            false
          );
        }

        this.filesBeingUploaded.value = [
          ...this.filesBeingUploaded.value.filter(
            (id: string) => id !== documentId
          ),
        ];
      }

      const mediaObject = await useUploadSubmissionToFirestore({
        file: wordLikeFile,
        documentId,
      });

      if (mediaObject == undefined) {
        this.filesBeingUploaded.value = [
          ...this.filesBeingUploaded.value.filter(
            (id: string) => id !== documentId
          ),
        ];
        continue;
      }

      await updateDocumentOriginalFileMediaHref(
        documentId,
        mediaObject.mediaHref
      );

      continue;
    }

    this.files = [];

    // if (onComplete != undefined) {
    //   onComplete(documentIds);
    // }

    if (this.requiresReview) {
      this.documentIds = documentIds;
      useSubmissionJobs().addSubmissionJob(this);
    }

    return documentIds;
  };
}
